/**
 * Zod Validation Schemas for Underwriting Config Forms
 *
 * Angular parity: underwriting-config forms use plain FormControls with **no**
 * required validators on keys. Empty keys are allowed at submit; backend governs
 * validity.
 *
 * `label`/`description`: accept empty strings. Loaded nulls normalize to `''`
 * at each tab's `reset()` call site.
 */

import { z } from 'zod'

/** Wire / Angular: `isLock` is 0 | 1. RHF checkbox may emit boolean before `setValueAs` runs (nested field-array paths). */
function normalizeCheckboxChildIsLock(val: unknown): number | undefined {
  if (val === undefined || val === null) return undefined
  if (typeof val === 'boolean') return val ? 1 : 0
  if (typeof val === 'number') return val === 1 ? 1 : 0
  if (typeof val === 'string') return val === '1' || val === 'true' ? 1 : 0
  return 0
}

export const checkboxChildSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  hasInput: z.boolean().optional(),
  isLock: z.preprocess(
    normalizeCheckboxChildIsLock,
    z.union([z.literal(0), z.literal(1)]).optional(),
  ),
})

export const checkboxSectionSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string().optional(),
  hasInput: z.boolean().optional(),
  child: z.array(checkboxChildSchema),
})

export const missingInfoFormSchema = z.object({
  checkbox_data: z.array(checkboxSectionSchema),
})

export type MissingInfoFormValues = z.infer<typeof missingInfoFormSchema>

export const deniedInfoFormSchema = z.object({
  checkbox_data: z.array(checkboxSectionSchema),
})

export type DeniedInfoFormValues = z.infer<typeof deniedInfoFormSchema>

export const checklistChildSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string().optional(),
  hasInput: z.boolean().optional(),
  isLock: z.preprocess(
    normalizeCheckboxChildIsLock,
    z.union([z.literal(0), z.literal(1)]).optional(),
  ),
})

export const checklistSectionSchema = z.object({
  key: z.string(),
  label: z.string(),
  child: z.array(checklistChildSchema),
})

export const checklistFormSchema = z.object({
  checkbox_data: z.array(checklistSectionSchema),
})

export type ChecklistFormValues = z.infer<typeof checklistFormSchema>
